import { Drawer, Loading } from "@components";
import ComboFilters from "pages/Reports/ComboFilters";
import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import classNames from "classnames/bind";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { ReportsAPI } from "providers/ReportsAPI";
import { If } from "shared/components/control-statements";
const css = classNames.bind(styles);

const statusWeights = {
  campeoes: 6,
  promissores: 5,
  requer_atencao: 4,
  em_risco: 3,
  desligando: 2,
  hibernando: 1,
  abandonados: 0,
};

const buildTable = (data) => {
  const statuses = {
    campeoes: `ERA "CAMPEÃO"`,
    promissores: `ERA "PROMISSOR"`,
    requer_atencao: `ERA "REQUER ATENÇÃO"`,
    em_risco: `ERA "EM RISCO"`,
    desligando: `ERA "DESLIGANDO"`,
    hibernando: `ERA "HIBERNANDO"`,
    abandonados: `ERA "ABANDONADO"`,
  };

  const statusKeys = Object.keys(statuses);

  const table = statusKeys.map((fromStatus) => {
    return {
      alias: fromStatus,
      status: statuses[fromStatus],
      values: statusKeys.map((toStatus) => {
        const key = `${fromStatus}__${toStatus}`;
        return {
          label: `VIROU ${statuses[toStatus].replace("ERA ", "")}`,
          tuple: key,
          value: data?.[key] ?? 0,
        };
      }),
    };
  });

  return table;
};

export default function TransitionsIdentificationCategories({ context }) {
  const provider = new ReportsAPI();

  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    getAllMonthsUntilNow(false)[0]
  );
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("quantity");

  const fetchHistory = async ({ year }) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.rfvHistory("transitions", year, null);
    const rows = Array(12).fill(0);

    results.forEach((result, index) => {
      const filteredResult = result[selectedFilter];
      rows[index] = filteredResult?.[selectedData.alias] || 0;
    });

    setLoadingHistoric(false);
    return rows;
  };

  const fetchData = async () => {
    const { brand, unit } = context || {};

    setLoading(true);
    const response = await provider.rfvTransitions(
      selectedMonth.year,
      selectedMonth.month,
      brand?.id,
      unit?.id
    );
    setRawData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, context]);

  const getShade = (value, max) => {
    if (!value || +value === 0) {
      return {
        background: "#1e1e1f",
        color: "#808080",
      };
    }

    const intensity = Math.min(Math.max(value / max, 0), 1);
    const alpha = 0.6 + intensity * 1;

    return {
      background: `rgba(97, 97, 255, ${intensity})`,
      color: `rgba(255, 255, 255, ${alpha})`,
    };
  };

  const months = getAllMonthsUntilNow(false);

  const { scrollRef, onMouseDown } = useDragScroll();

  const isDowngradeFromTuple = (tuple) => {
    if (!tuple) return false;
    const [from, to] = tuple.split("__");

    const fromWeight = statusWeights[from];
    const toWeight = statusWeights[to];

    return toWeight < fromWeight;
  };

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Transições entre perfis RFV</p>
          <span
            className={styles.info}
            title="Quantidade de clientes que transicionaram de um perfil RFV para outro."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>

        <div className="d-flex align-center">
          <div className="d-flex align-center gap-10">
            <div
              className={css({
                selectMini: true,
                selected: selectedFilter === "quantity",
              })}
              onClick={() => setSelectedFilter("quantity")}
            >
              Qtd. clientes
            </div>
            <div
              className={css({
                selectMini: true,
                selected: selectedFilter === "recency",
              })}
              onClick={() => setSelectedFilter("recency")}
            >
              Tempo de transição
            </div>
          </div>
          <div className={styles.divider} />
          <div className="d-flex align-center">
            <ComboFilters
              options={months}
              onSelect={(option) => setSelectedMonth(option)}
              selectedOption={selectedMonth}
            />
          </div>
        </div>
      </div>

      <div className={styles.hairline} />

      {loading ? (
        <div
          className="d-flex align-center justify-center"
          style={{ height: 307 }}
        >
          <Loading />
        </div>
      ) : (
        <div
          className={styles.tableTransitions}
          onMouseDown={onMouseDown}
          ref={scrollRef}
        >
          <table>
            <thead>
              <tr>
                <th
                  style={{
                    width: "15%",
                    backgroundColor: "#1F1F1F",
                    verticalAlign: "middle",
                    color: "#4C4C4C",
                  }}
                >
                  <i className="far fa-arrow-turn-right" />
                </th>
                <th style={{ width: "21.25%" }}>VIROU "CAMPEÃO"</th>
                <th style={{ width: "21.25%" }}>VIROU "PROMISSOR"</th>
                <th style={{ width: "21.25%" }}>VIROU "REQUER ATENÇÃO"</th>
                <th style={{ width: "21.25%" }}>VIROU "EM RISCO"</th>
                <th style={{ width: "21.25%" }}>VIROU "DESLIGANDO"</th>
                <th style={{ width: "21.25%" }}>VIROU "HIBERNANDO"</th>
                <th style={{ width: "21.25%" }}>VIROU "ABANDONADO"</th>
              </tr>
            </thead>
            <tbody>
              {buildTable(rawData?.[selectedFilter]).map((row) => (
                <tr key={row.status}>
                  <td className={styles.status} style={{ minWidth: 300 }}>
                    {row.status}
                  </td>
                  {row.values.map(({ tuple, label, value }, idx) => {
                    const allValues = buildTable(
                      rawData?.[selectedFilter]
                    ).flatMap((item) => item.values);

                    const sameColunRow = (str) => {
                      const [before, after] = str.split("__");
                      return before === after;
                    };

                    const maxItem = allValues
                      .filter((item) => !sameColunRow(item.tuple))
                      .reduce((max, item) => {
                        return Number(item.value) > Number(max.value)
                          ? item
                          : max;
                      });

                    let style;

                    if (sameColunRow(tuple)) {
                      style = {
                        background: "transparent",
                        color: "#808080",
                      };
                    } else style = getShade(value, Number(maxItem.value));

                    return (
                      <td
                        key={idx}
                        style={{
                          backgroundColor: style.background,
                          minWidth: 200,
                          color: style.color,
                          ...(value && { cursor: "pointer" }),
                          ...(value &&
                            tuple === hovered?.alias && {
                              filter: "brightness(1.1)",
                            }),
                        }}
                        onMouseEnter={() => {
                          setHovered({
                            alias: `${tuple}`,
                            name: `${row.status}`,
                          });
                        }}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => {
                          if (value && !sameColunRow(tuple))
                            setSelectedData({
                              alias: `${tuple}`,
                              name: `${row.status} E ${label}`,
                            });
                        }}
                      >
                        <If cond={sameColunRow(tuple)}>---</If>
                        <If cond={!sameColunRow(tuple)}>
                          {selectedFilter === "quantity"
                            ? value ?? "---"
                            : value
                            ? `${value} dias`
                            : "---"}
                        </If>

                        <div
                          className={styles.chart}
                          style={{
                            ...(value &&
                              tuple === hovered?.alias &&
                              !sameColunRow(tuple) && {
                                transform: "translateY(0)",
                                opacity: 1,
                              }),
                          }}
                        >
                          <i className="far fa-chart-waterfall" />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          enableMonth={false}
          onClose={() => setSelectedData(false)}
          loading={loadingHistoric}
          onFetchData={(...params) => fetchHistory(...params)}
          startOn2025
          invertColorSign={isDowngradeFromTuple(selectedData?.alias)}
        />
      </Drawer>
    </div>
  );
}
